import {Icon} from '@wandb/components';
import {Switch} from '@wandb/weave/components';
import {Button} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import React, {useState} from 'react';

import {type SortOption} from '../types';

type NamedWorkspaceSortOptionSelectorProps = {
  selectedOption: SortOption;
  onSelectOption: (option: SortOption) => void;
  ariaLabel?: string;
  shouldShowMyViewsOnly?: boolean;
  onShowMyViewsOnly?: (show: boolean) => void;
};

type ItemLabelProps = {
  isSelected: boolean;
  label: string;
};

const ItemLabel: React.FC<ItemLabelProps> = ({isSelected, label}) => (
  <div className="flex w-full justify-between">
    <span className="truncate text-wb-text-primary dark:text-moon-300">
      {label}
    </span>
    {isSelected && <Icon icon="checkmark" />}
  </div>
);

type SortMenuItemProps = {
  option: SortOption;
  selectedOption: SortOption;
  onSelectOption: (option: SortOption) => void;
  label: string;
};

const SortMenuItem: React.FC<SortMenuItemProps> = ({
  option,
  selectedOption,
  onSelectOption,
  label,
}) => (
  <DropdownMenu.Item
    onSelect={() => onSelectOption(option)}
    className="flex items-center justify-between">
    <ItemLabel isSelected={selectedOption === option} label={label} />
  </DropdownMenu.Item>
);

export const NamedWorkspaceSortOptionSelector: React.FC<
  NamedWorkspaceSortOptionSelectorProps
> = ({
  selectedOption,
  onSelectOption,
  ariaLabel = 'Saved view options',
  shouldShowMyViewsOnly,
  onShowMyViewsOnly,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DropdownMenu.Root
      open={isOpen}
      onOpenChange={setIsOpen}
      data-testid="sort-option-selector">
      <DropdownMenu.Trigger
        className="flex items-center"
        onClick={() => setIsOpen(!isOpen)}>
        <Button
          aria-label={ariaLabel}
          variant="ghost"
          icon="overflow-horizontal"
          active={isOpen}
        />
      </DropdownMenu.Trigger>

      <DropdownMenu.Content className="w-[13rem]" align="start">
        <DropdownMenu.Label className="flex gap-8 px-12 py-8 text-base font-semibold text-wb-text-primary dark:text-moon-50">
          Sorting
        </DropdownMenu.Label>

        <SortMenuItem
          option="alphabetical"
          selectedOption={selectedOption}
          onSelectOption={onSelectOption}
          label="Alphabetical"
        />

        <SortMenuItem
          option="lastSaved"
          selectedOption={selectedOption}
          onSelectOption={onSelectOption}
          label="Last saved"
        />

        <SortMenuItem
          option="lastCreated"
          selectedOption={selectedOption}
          onSelectOption={onSelectOption}
          label="Last created"
        />

        {onShowMyViewsOnly != null && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Item
              onSelect={e => {
                setIsOpen(true);
                e.preventDefault();
              }}
              className="flex items-center justify-between">
              <span className="text-wb-text-primary dark:text-moon-300">
                Show my views only
              </span>
              <Switch.Root
                id="switch"
                checked={shouldShowMyViewsOnly ?? false}
                size="small"
                onCheckedChange={onShowMyViewsOnly}>
                <Switch.Thumb
                  checked={shouldShowMyViewsOnly ?? false}
                  size="small"
                />
              </Switch.Root>
            </DropdownMenu.Item>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
